/**
 * Notification Center Component
 * Displays notifications with filtering and actions
 */

import React, { useState } from 'react';
import { Notification } from '../types';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import {
  Bell,
  BellDot,
  Check,
  CheckCheck,
  Clock,
  AlertCircle,
  AlertTriangle,
  Info,
  MessageSquare,
  UserPlus,
  Activity,
  Filter,
  X,
} from 'lucide-react';
import { useNavigate } from 'react-router';
import { parseDateLike } from '../utils/dateUtils';
import {
  NOTIFICATION_DISMISS_BUTTON_CLASS,
  NOTIFICATION_DIVIDER_CLASS,
  NOTIFICATION_FILTER_BAR_CLASS,
  NOTIFICATION_HEADER_BORDER_CLASS,
  NOTIFICATION_ITEM_CLASS,
  NOTIFICATION_ITEM_MESSAGE_READ_CLASS,
  NOTIFICATION_ITEM_MESSAGE_UNREAD_CLASS,
  NOTIFICATION_ITEM_READ_CLASS,
  NOTIFICATION_ITEM_TITLE_READ_CLASS,
  NOTIFICATION_ITEM_TITLE_UNREAD_CLASS,
  NOTIFICATION_ITEM_UNREAD_CLASS,
  NOTIFICATION_MUTED_TEXT_CLASS,
  NOTIFICATION_PANEL_CLASS,
  NOTIFICATION_TIMESTAMP_CLASS,
  NOTIFICATION_UNREAD_DOT_CLASS,
} from '../constants/notificationTheme';

const toTime = (value: unknown, fallback: number = 0): number => {
  const parsedDate = parseDateLike(value);
  return parsedDate ? parsedDate.getTime() : fallback;
};

interface NotificationCenterProps {
  notifications: Notification[];
  onMarkAsRead: (notificationId: string) => void;
  onMarkAllAsRead: () => void;
  onDismiss: (notificationId: string) => void;
  onClose?: () => void;
}

export default function NotificationCenter({
  notifications,
  onMarkAsRead,
  onMarkAllAsRead,
  onDismiss,
  onClose,
}: NotificationCenterProps) {
  const navigate = useNavigate();
  const [filter, setFilter] = useState<'all' | 'unread' | 'urgent'>('all');

  const unreadCount = notifications.filter(n => !n.read).length;

  const filteredNotifications = notifications.filter(notif => {
    if (filter === 'unread') return !notif.read;
    if (filter === 'urgent') return notif.priority === 'urgent' || notif.priority === 'high';
    return true;
  });

  const getNotificationIcon = (type: Notification['type'], priority: Notification['priority']) => {
    const iconClass =
      priority === 'urgent'
        ? 'text-destructive'
        : priority === 'high'
          ? 'text-chart-5'
          : 'text-primary';
    
    switch (type) {
      case 'deadline':
        return <AlertTriangle className={`w-5 h-5 ${iconClass}`} />;
      case 'update_overdue':
        return <Clock className={`w-5 h-5 ${iconClass}`} />;
      case 'reminder':
        return <Bell className={`w-5 h-5 ${iconClass}`} />;
      case 'assignment':
        return <UserPlus className="w-5 h-5 text-primary" />;
      case 'status_change':
        return <Activity className="w-5 h-5 text-primary" />;
      case 'comment':
      case 'mention':
        return <MessageSquare className="w-5 h-5 text-primary" />;
      default:
        return <Info className={`w-5 h-5 ${iconClass}`} />;
    }
  };

  const getPriorityBadge = (priority: Notification['priority']) => {
    const colors = {
      urgent: 'border-destructive/40 bg-destructive/15 text-destructive',
      high: 'border-chart-5/40 bg-chart-5/10 text-chart-5',
      medium: 'border-primary/30 bg-primary/10 text-primary',
      low: 'bg-muted text-foreground border-border',
    };

    return (
      <Badge variant="outline" className={`${colors[priority]} text-xs`}>
        {priority}
      </Badge>
    );
  };

  const handleNotificationClick = (notif: Notification) => {
    if (!notif.read) {
      onMarkAsRead(notif.id);
    }
    if (notif.actionUrl) {
      navigate(notif.actionUrl);
      if (onClose) onClose();
    }
  };

  const formatTimeAgo = (date: string) => {
    const now = new Date();
    const then = parseDateLike(date);
    if (!then) return 'Unknown date';

    const seconds = Math.floor((now.getTime() - then.getTime()) / 1000);

    if (seconds < 60) return 'Just now';
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
    if (seconds < 604800) return `${Math.floor(seconds / 86400)}d ago`;
    return then.toLocaleDateString();
  };

  return (
    <Card className={`flex max-h-[80vh] w-full max-w-2xl flex-col ${NOTIFICATION_PANEL_CLASS}`}>
      <CardHeader className={`flex flex-row items-center justify-between space-y-0 border-b pb-4 ${NOTIFICATION_HEADER_BORDER_CLASS}`}>
        <div className="flex items-center gap-3">
          <BellDot className="h-6 w-6 text-primary" />
          <div>
            <CardTitle>Notifications</CardTitle>
            {unreadCount > 0 && (
              <p className={`mt-1 text-sm ${NOTIFICATION_MUTED_TEXT_CLASS}`}>{unreadCount} unread</p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {unreadCount > 0 && (
            <Button variant="ghost" size="sm" onClick={onMarkAllAsRead}>
              <CheckCheck className="w-4 h-4 mr-1" />
              Mark all read
            </Button>
          )}
          {onClose && (
            <Button variant="ghost" size="sm" onClick={onClose}>
              <X className="w-4 h-4" />
            </Button>
          )}
        </div>
      </CardHeader>

      {/* Filters */}
      <div className={`flex items-center gap-2 border-b px-6 py-3 ${NOTIFICATION_FILTER_BAR_CLASS}`}>
        <Filter className={`h-4 w-4 ${NOTIFICATION_MUTED_TEXT_CLASS}`} />
        <Button
          variant={filter === 'all' ? 'default' : 'ghost'}
          size="sm"
          onClick={() => setFilter('all')}
        >
          All ({notifications.length})
        </Button>
        <Button
          variant={filter === 'unread' ? 'default' : 'ghost'}
          size="sm"
          onClick={() => setFilter('unread')}
        >
          Unread ({unreadCount})
        </Button>
        <Button
          variant={filter === 'urgent' ? 'default' : 'ghost'}
          size="sm"
          onClick={() => setFilter('urgent')}
        >
          Urgent ({notifications.filter(n => n.priority === 'urgent' || n.priority === 'high').length})
        </Button>
      </div>

      {/* Notifications List */}
      <CardContent className="flex-1 overflow-y-auto p-0">
        {filteredNotifications.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <Bell className={`mb-3 h-12 w-12 ${NOTIFICATION_MUTED_TEXT_CLASS}`} />
            <h3 className="mb-1 text-lg font-semibold">No notifications</h3>
            <p className={`text-sm ${NOTIFICATION_MUTED_TEXT_CLASS}`}>
              {filter === 'unread' ? "You're all caught up!" : 'Notifications will appear here'}
            </p>
          </div>
        ) : (
          <div className={`divide-y ${NOTIFICATION_DIVIDER_CLASS}`}>
            {filteredNotifications
              .sort((a, b) => toTime(b.createdAt) - toTime(a.createdAt))
              .map(notif => (
                <div
                  key={notif.id}
                  className={`${NOTIFICATION_ITEM_CLASS} ${
                    !notif.read ? NOTIFICATION_ITEM_UNREAD_CLASS : NOTIFICATION_ITEM_READ_CLASS
                  }`}
                  onClick={() => handleNotificationClick(notif)}
                >
                  <div className="flex gap-3">
                    <div className="flex-shrink-0 mt-1">
                      {getNotificationIcon(notif.type, notif.priority)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2 mb-1">
                        <h4 className={`text-sm font-semibold ${!notif.read ? NOTIFICATION_ITEM_TITLE_UNREAD_CLASS : NOTIFICATION_ITEM_TITLE_READ_CLASS}`}>
                          {notif.title}
                        </h4>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          {getPriorityBadge(notif.priority)}
                          {!notif.read && (
                            <div className={NOTIFICATION_UNREAD_DOT_CLASS} />
                          )}
                        </div>
                      </div>
                      <p className={`mb-2 text-sm ${!notif.read ? NOTIFICATION_ITEM_MESSAGE_UNREAD_CLASS : NOTIFICATION_ITEM_MESSAGE_READ_CLASS}`}>
                        {notif.message}
                      </p>
                      <div className="flex items-center justify-between">
                        <span className={`text-xs ${NOTIFICATION_TIMESTAMP_CLASS}`}>
                          {formatTimeAgo(notif.createdAt)}
                        </span>
                        <div className="flex items-center gap-2">
                          {!notif.read && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                onMarkAsRead(notif.id);
                              }}
                              className="h-7 text-xs"
                            >
                              <Check className="w-3 h-3 mr-1" />
                              Mark read
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              onDismiss(notif.id);
                            }}
                            className={NOTIFICATION_DISMISS_BUTTON_CLASS}
                          >
                            <X className="w-3 h-3" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
